import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const STEPS = [
  {
    name: "Discovery",
    days: "Days 1–3",
    title: "We learn how your firm actually runs.",
    summary:
      "Not how software wishes it did. We map your intake, your phone flow, and your rules before we build anything.",
    bullets: [
      "Map your intake, phone flow, and rules",
      "Review your CMS and current stack",
      "Define what 'good' sounds like for your firm",
    ],
  },
  {
    name: "Build",
    days: "Days 4–9",
    title: "We wire it to your stack and your voice.",
    summary:
      "Integrations, data mapping, and a tailored workflow — tuned to how your firm sounds, so nothing gets re-keyed.",
    bullets: [
      "Connect your CMS and phone system",
      "Map data so nothing is re-keyed",
      "Tune greeting, questions, and routing",
    ],
  },
  {
    name: "Test",
    days: "Days 10–12",
    title: "You hear it before a single real caller does.",
    summary:
      "Sandbox calls you can listen to and adjust. You set the confidence line and review transcripts until it sounds right.",
    bullets: [
      "Listen to sandbox calls",
      "Set your confidence line, review transcripts",
      "Adjust until it sounds like your firm",
    ],
  },
  {
    name: "Live",
    days: "~Day 14",
    title: "It answers as your firm — human in control.",
    summary:
      "Go live, monitored around the clock, with pause, override, and rollback in one click. It gets sharper every week.",
    bullets: [
      "Go live, monitored 24/7",
      "Pause, override, or roll back anytime",
      "Weekly review as it gets sharper",
    ],
  },
];

export const HowSlide = ({ slide }) => {
  const reduce = useReducedMotion();
  const [selected, setSelected] = useState(0);
  const [lit, setLit] = useState(reduce ? STEPS.length : 0);
  const [fill, setFill] = useState(reduce ? 100 : 0);
  const dotRefs = useRef([]);
  const timers = useRef([]);

  useEffect(() => {
    if (reduce) return;
    const raf = requestAnimationFrame(() => setFill(100));
    STEPS.forEach((_, i) => {
      timers.current.push(setTimeout(() => setLit(i + 1), 250 + i * 400));
    });
    return () => {
      cancelAnimationFrame(raf);
      timers.current.forEach((t) => clearTimeout(t));
      timers.current = [];
    };
  }, [reduce]);

  const onKey = useCallback(
    (e) => {
      let n = null;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") n = Math.min(selected + 1, STEPS.length - 1);
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") n = Math.max(selected - 1, 0);
      else if (e.key === "Home") n = 0;
      else if (e.key === "End") n = STEPS.length - 1;
      if (n !== null) {
        e.preventDefault();
        e.stopPropagation();
        setSelected(n);
        dotRefs.current[n]?.focus();
      }
    },
    [selected]
  );

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
  };

  const s = STEPS[selected];

  return (
    <Stage testId={`slide-${slide?.n ?? 10}`}>
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(18px,3.6%,40px)]"
      >
        {/* Top row */}
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>How it works</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-ink-muted tabular-nums">
            10 / 11
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Live in about two weeks. Pilot before you commit.
        </motion.h1>
        <motion.p variants={item} className="font-sans text-[14px] text-ink-soft mt-2 max-w-[66ch]">
          No rip-and-replace. We map your firm, wire it to your stack, and prove it
          in a sandbox — before a single real caller ever hears it.
        </motion.p>

        {/* Timeline */}
        <motion.div
          variants={item}
          role="tablist"
          aria-label="Onboarding milestones"
          onKeyDown={onKey}
          className="relative mt-8 px-4"
        >
          {/* track */}
          <div className="absolute left-4 right-4 top-3 h-px bg-hairline" />
          <div
            className="absolute left-4 top-3 h-px bg-accent transition-[width] duration-[1600ms] ease-out"
            style={{ width: `calc((100% - 2rem) * ${fill / 100})` }}
          />
          <div className="relative flex justify-between">
            {STEPS.map((st, i) => {
              const isLit = i < lit;
              const on = i === selected;
              return (
                <button
                  key={i}
                  ref={(el) => (dotRefs.current[i] = el)}
                  role="tab"
                  aria-selected={on}
                  tabIndex={on ? 0 : -1}
                  data-testid={`milestone-${i + 1}`}
                  onClick={() => setSelected(i)}
                  className="flex flex-col items-center gap-2 outline-none group"
                  style={{ width: "25%" }}
                >
                  <span
                    className={`relative w-6 h-6 rounded-full flex items-center justify-center font-mono text-[10px] transition-all duration-300 ${
                      on
                        ? "bg-accent text-white scale-125 shadow-[0_0_0_5px_rgba(30,91,255,0.18)]"
                        : isLit
                        ? "bg-accent text-white"
                        : "bg-paper border border-hairline text-ink-muted"
                    } group-focus-visible:ring-2 group-focus-visible:ring-accent group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-[#F7F6F5]`}
                  >
                    {i + 1}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-accent">
                    {st.days}
                  </span>
                  <span
                    className={`font-sans text-sm font-semibold transition-colors ${
                      on ? "text-accent" : "text-ink"
                    }`}
                  >
                    {st.name}
                  </span>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Detail card */}
        <motion.div
          variants={item}
          key={selected}
          data-testid="phase-detail"
          className="rounded-2xl border border-hairline bg-[#FBFAF6] p-6 mt-6 grid grid-cols-1 min-[900px]:grid-cols-2 gap-6"
        >
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-accent">
              Phase {selected + 1} · {s.days}
            </div>
            <h3
              data-testid="phase-title"
              className="font-display font-medium text-2xl text-ink mt-2 leading-snug"
            >
              {s.title}
            </h3>
            <p className="font-sans text-[14px] text-ink-soft leading-relaxed mt-3 max-w-md">
              {s.summary}
            </p>
          </div>
          <div className="min-[900px]:border-l min-[900px]:border-hairline min-[900px]:pl-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted mb-3">
              What happens
            </div>
            <div className="space-y-2.5">
              {s.bullets.map((b, k) => (
                <div key={k} className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-accent mt-[7px] shrink-0" />
                  <span className="font-sans text-[14px] text-ink-soft leading-snug">{b}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Pilot banner */}
        <motion.div
          variants={item}
          className="mt-auto pt-5"
        >
          <div className="rounded-2xl px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-4 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]">
            <span className="w-11 h-11 rounded-full bg-[linear-gradient(135deg,#1E5BFF,#6FA0FF)] flex items-center justify-center shrink-0 text-white text-lg">
              ◆
            </span>
            <div className="flex-1">
              <h3 className="font-display font-medium text-xl text-paper leading-tight">
                Start with a pilot.
              </h3>
              <p className="font-sans text-[13px] text-paper/70 mt-0.5">
                Go live, see it work on real calls, and keep going only if it earns
                its place.
              </p>
            </div>
            <div className="text-left sm:text-right shrink-0">
              <div className="font-mono text-xl text-[#93B4FF] tabular-nums">~14 days</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-paper/50">
                discovery → live
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default HowSlide;
